'use client';

import { useState, useEffect } from 'react';
import { trackLead } from '@/lib/analytics';

const MODEL = [
  { t: 'Henvendelser direkte til deg', d: 'Markedsplassen er inkludert — klienter tar kontakt direkte, uten stykkpris per henvendelse.' },
  { t: 'Ingen provisjon', d: 'Vi tar ingen prosent av det du tjener på klientene dine.' },
  { t: 'Markedsføring inkludert', d: 'Vi setter deg foran folk som faktisk leter etter en coach.' },
  { t: 'Ingen binding', d: '1 måneds oppsigelse. Du eier dine egne data og kan ta dem med deg.' },
];

const TOOLS = ['Trening & kosthold', 'Meldinger', 'Kalender & booking', 'Betaling', 'Fremgang & bilder', 'Skjemaer'];

const TOOLS_FULL: [string, string][] = [
  ['Treningsprogram', 'Bygg og del programmer med ferdig øvelsesbibliotek.'],
  ['Måltidsplan & oppskrifter', 'Lag planer — makroer regnes ut automatisk.'],
  ['Meldinger', 'All klient-kommunikasjon samlet på ett sted.'],
  ['Kalender & booking', 'Klienten booker selv i din ledige tid.'],
  ['Betaling', 'Integrert betaling — slutt med Vipps-purring.'],
  ['Helse & målinger', 'Vekt, mål og helsedata per klient med graf.'],
  ['Fremgangsbilder', 'Før/etter samlet og enkelt å sammenligne.'],
  ['Vaner & mål', 'Vane-tracker med streaks og måloppfølging.'],
  ['Skjemaer & intake', 'Digitale spørreskjemaer klienten fyller ut selv.'],
];

function Compass({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
      <circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1" />
      <circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7" />
      <polygon points="16,6 17.5,13 16,12 14.5,13" fill="#1c1917" />
      <polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c" />
      <path d="M16,5 A11,11 0 1,0 16,27" stroke="#a8a29e" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export default function CoachStartClient() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [foundingLoading, setFoundingLoading] = useState(false);
  const [foundingClaimed, setFoundingClaimed] = useState(false);
  const [utm, setUtm] = useState<Record<string, string>>({});

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setUtm({
      source: p.get('utm_source') || '',
      medium: p.get('utm_medium') || '',
      campaign: p.get('utm_campaign') || '',
      content: p.get('utm_content') || '',
    });
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'coach', source: 'lp-coach', utm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Noe gikk galt');
      setSubmitted(true);
      setShowModal(true);
      trackLead('generate_lead', { email, content_name: 'coach_interesse', method: 'lp-coach' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  function openDemo() {
    try {
      localStorage.setItem('cg_session_role', 'coach');
      localStorage.setItem('cg_session_mock', '1');
      (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.('event', 'coach_demo_open');
    } catch { /* ignore */ }
    window.open('/coach', '_blank');
  }

  async function claimFounding() {
    setFoundingLoading(true);
    try {
      await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'coach', source: 'coach-founding', founding: true, utm }),
      });
      setFoundingClaimed(true);
      trackLead('generate_lead', { email, content_name: 'coach_founding', method: 'lp-coach-founding' });
    } catch {
      /* leaden er allerede fanget i steg 1 — founding er best effort */
      setFoundingClaimed(true);
    } finally {
      setFoundingLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-stone-50 text-stone-900">
      {/* Hero */}
      <section className="px-6 pt-6 pb-16 max-w-3xl mx-auto text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Compass />
          <span className="text-lg font-semibold tracking-tight">CoachGuiden</span>
        </div>

        <p className="inline-block text-sm uppercase tracking-[0.15em] text-orange-700 bg-orange-50 border border-orange-100 rounded-full px-5 py-2 mb-6">
          For PT-er og online coacher
        </p>
        <h1 className="text-4xl md:text-7xl font-light tracking-tight leading-[1.08] mb-5">
          Mindre styr.<br />Mer coaching.
        </h1>
        <p className="text-lg md:text-2xl text-stone-600 max-w-2xl mx-auto mb-7 leading-relaxed">
          Alt coachingen din trenger på ett sted — trening, kosthold, meldinger, kalender og betaling.
          Slutt med fem apper og Vipps-purring.
        </p>

        {submitted ? (
          <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-3xl p-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-emerald-50 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-light mb-3">Takk for interessen!</h2>
            <p className="text-lg text-stone-500">
              {foundingClaimed
                ? 'Vi holder av en plass til deg og tar kontakt med onboarding.'
                : 'Vi tar kontakt med onboarding og oppsett.'}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="din@epost.no"
              aria-label="E-postadresse"
              className="flex-1 px-6 py-5 rounded-full border border-stone-300 bg-white text-stone-900 text-lg placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-stone-900 hover:bg-stone-800 disabled:opacity-60 text-white px-8 py-5 rounded-full font-medium text-lg whitespace-nowrap transition-all hover:scale-[1.02]"
            >
              {loading ? 'Sender…' : 'Meld interesse'}
            </button>
          </form>
        )}
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        {!submitted && (
          <div className="mt-5">
            <button onClick={() => setShowTools(true)} className="text-lg text-stone-700 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors font-medium">
              Se alle verktøy og demo
            </button>
            <p className="text-base text-stone-500 mt-3">For PT-er og online coacher · Uforpliktende</p>
          </div>
        )}
      </section>

      {/* Modell */}
      <section className="px-6 py-14 bg-white border-y border-stone-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-3xl md:text-4xl font-light tracking-tight mb-12">Bygd for coacher — ikke for admin</h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {MODEL.map((m) => (
              <div key={m.t} className="flex gap-4">
                <svg className="w-7 h-7 text-emerald-600 flex-none mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-medium text-xl mb-1">{m.t}</h3>
                  <p className="text-lg text-stone-500 leading-relaxed">{m.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alt på ett sted */}
      <section className="px-6 py-14 max-w-3xl mx-auto text-center">
        <p className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-4">Alt på ett sted</p>
        <div className="flex flex-wrap justify-center gap-3">
          {TOOLS.map((t) => (
            <span key={t} className="px-6 py-3 rounded-full bg-white border border-stone-200 text-stone-700 font-medium text-lg">{t}</span>
          ))}
        </div>
      </section>

      {/* Founding-CTA */}
      {!submitted && (
        <section className="px-6 py-16 bg-stone-900 text-white text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-orange-300 mb-4">For de 20 første coachene</p>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-4">1 måned gratis + 20 % livstidsrabatt</h2>
          <p className="text-lg text-stone-300 mb-8 max-w-md mx-auto">De 20 første coachene får 1 måned gratis og 20 % rabatt for alltid. Meld interesse nå, så holder vi av en plass til deg.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="inline-block bg-white text-stone-900 px-8 py-5 rounded-full font-medium text-lg hover:bg-stone-100 transition-all hover:scale-[1.02]">
            Meld interesse
          </a>
        </section>
      )}

      <footer className="px-6 py-10 text-center text-base text-stone-400">coachguiden.no</footer>

      {/* Founding-modal (steg 2) */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-8 relative text-center">
            <button onClick={() => setShowModal(false)} aria-label="Lukk" className="absolute top-4 right-6 text-stone-400 hover:text-stone-900 text-2xl leading-none">×</button>
            {foundingClaimed ? (
              <>
                <div className="w-14 h-14 mx-auto mb-4 bg-emerald-50 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-3xl font-light mb-3">Plassen din er holdt av.</h3>
                <p className="text-lg text-stone-500 mb-6">Vi tar kontakt med oppsett. Velkommen som en av de første coachene!</p>
                <button onClick={() => setShowModal(false)} className="bg-stone-900 text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-stone-800 transition-colors">Lukk</button>
              </>
            ) : (
              <>
                <p className="text-sm uppercase tracking-[0.15em] text-orange-700 mb-3">Takk for interessen!</p>
                <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-3">Vil du sikre deg en av de 20 plassene?</h3>
                <p className="text-lg text-stone-500 mb-2">De 20 første coachene får <span className="text-stone-900 font-medium">1 måned gratis + 20 % livstidsrabatt</span>.</p>
                <p className="text-base text-stone-400 mb-6">Kun 20 plasser totalt.</p>
                <button
                  onClick={claimFounding}
                  disabled={foundingLoading}
                  className="w-full bg-stone-900 text-white py-5 rounded-full font-medium text-lg hover:bg-stone-800 disabled:opacity-60 transition-colors mb-3"
                >
                  {foundingLoading ? 'Holder av…' : 'Sikre plassen min'}
                </button>
                <button onClick={() => setShowModal(false)} className="text-base text-stone-400 hover:text-stone-700 transition-colors">Nei takk, bare meld interesse</button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Verktøy- + demo-modal */}
      {showTools && (
        <div
          className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center px-6 py-10"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTools(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 relative max-h-[85vh] overflow-y-auto">
            <button onClick={() => setShowTools(false)} aria-label="Lukk" className="absolute top-4 right-6 text-stone-400 hover:text-stone-900 text-2xl leading-none">×</button>
            <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-1">Alt coachingen din trenger</h3>
            <p className="text-stone-500 text-base mb-6">Ett system — i stedet for fem apper og regneark.</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 mb-7">
              {TOOLS_FULL.map(([t, d]) => (
                <div key={t} className="flex gap-3">
                  <svg className="w-6 h-6 text-emerald-600 flex-none mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <div className="font-medium text-base text-stone-900">{t}</div>
                    <div className="text-stone-500 text-base leading-snug">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={openDemo} className="w-full bg-stone-900 text-white py-5 rounded-full font-medium text-lg hover:bg-stone-800 transition-colors">
              Åpne demo-dashbord →
            </button>
            <p className="text-sm text-stone-400 text-center mt-3">Demo med eksempeldata — samme dashbord som betalende coacher.</p>
          </div>
        </div>
      )}
    </main>
  );
}
